import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@material-ui/core";

import {
  getArchivePenalties,
  getHuntersMonthPlans,
  getHuntersReports,
} from "./api";
import NumberFormat from "react-number-format";
import Progress from "components/UI/Progress";
import Layout from "../../components/layout";
import Filters from "./Filters";
import EmptyOrShow from "../../components/EmptyOrShow";
import Search from "../../components/UI/Search";
import cookiesService from "../../services/cookiesService";
import { HuntersAccordion } from "./accordion";
import { useHuntersStyles } from "../huntersKpi";
import {
  clampToV1Month,
  hunterDetailsPath,
  readStoredV1Period,
  writeStoredV1Period,
} from "../huntersKpi/cutover";
import { useDispatch } from "react-redux";
import { setShowModal } from "../../services/redux/actions/app-actions";
import { MODALS } from "../../constants";

const cookiesServiceInstance = cookiesService.getService();

const HuntersKpiArchiveWrapper = () => {
  const { position_code } = cookiesServiceInstance.getUserData();
  const history = useHistory();

  if (position_code !== "SALE_HEAD_HEAD") {
    history.push("/");
    return null;
  }
  return <HuntersKpiArchive />;
};

const initialV1Filters = () => {
  const stored = readStoredV1Period();
  const clamped = clampToV1Month(
    stored.year || new Date().getFullYear(),
    stored.month || new Date().getMonth() + 1
  );
  return {
    year: String(clamped.year),
    month: String(clamped.month),
    sale_department_id: "",
  };
};

const HuntersKpiArchive = () => {
  const classes = useHuntersStyles();
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const [stateSum, setStateSum] = useState({});
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState(initialV1Filters);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    writeStoredV1Period(filters.year, filters.month);
  }, [filters.year, filters.month]);

  const handleSearchChange = useCallback(() => {
    if (!search) return setFilteredData(data);
    setFilteredData(sortData(data, search));
  }, [search, data]);

  useEffect(() => {
    const timeout = setTimeout(() => handleSearchChange(), 500);
    return () => {
      clearTimeout(timeout);
    };
  }, [handleSearchChange]);

  useEffect(() => {
    setStateSum(calcSum(filteredData));
  }, [filteredData]);

  const fetchData = useCallback(() => {
    setLoading(true);
    // Report has hunter_salary but not hunter_month_plan_id — merge from plans GET.
    Promise.all([
      getHuntersReports(filters),
      getHuntersMonthPlans(filters).catch(() => []),
    ])
      .then(([report, plans]) => {
        const planIdByEmployee = new Map(
          (plans || []).map((p) => [p.employee_id, p.hunter_month_plan_id])
        );
        const merged = (report || []).map((row) => ({
          ...row,
          hunter_month_plan_id: planIdByEmployee.get(row.employee_id) || null,
        }));
        setData(merged);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const next = { ...filters, [name]: value };
    if (name === "year" || name === "month") {
      const clamped = clampToV1Month(next.year, next.month);
      next.year = String(clamped.year);
      next.month = String(clamped.month);
    }
    setFilters(next);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const openPenalties = (row) => {
    if (!row.hunter_month_plan_id) return;
    dispatch(
      setShowModal(true, MODALS.penaltyListModal, {
        title: "Список штрафов",
        id: row.hunter_month_plan_id,
        state: filters,
        fio: row.fio,
        readOnly: true,
        fetchPenalties: (planId) => getArchivePenalties(planId),
      })
    );
  };

  return (
    <Layout title="KPI (архив)">
      <Box
        sx={{
          padding: "45px 20px 20px",
        }}
      >
        <Filters state={filters} handleChange={handleChange} />

        <HuntersAccordion filters={filters} />

        <div style={{ marginBottom: "25px", width: "250px" }}>
          <Search
            placeholder={"Поиск"}
            handleChange={handleSearch}
            value={search}
          />
        </div>
        <EmptyOrShow loading={loading}>
          <TableContainer className={classes.table_container}>
            <Table size="small" stickyHeader aria-label="sticky table">
              <TableHead className={classes.table_head}>
                <TableRow>
                  {headTitle.map((el) => {
                    return (
                      <TableCell key={el} style={{ fontWeight: "bold" }}>
                        {el}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody className={classes.table_body}>
                {filteredData.map((e) => {
                  return (
                    <TableRow
                      key={e.employee_id}
                      className={!e.with_plan ? classes.table_item_false : ""}
                    >
                      <TableCell align="left">
                        {e.sale_department_name}
                      </TableCell>
                      <TableCell align="left">
                        <Link
                          to={hunterDetailsPath(
                            e.employee_id,
                            filters.year,
                            filters.month
                          )}
                          target="_blank"
                        >
                          {e.fio}
                        </Link>
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.conversion_total_cnt}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.contracts_cnt}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        {`${e?.conversion_pct ?? 0}%`}
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.hunter_month_plan_sum}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.fact_opt}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.fact_ddu}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <Progress
                          progress={e.percentage}
                          title="План"
                          color={e.percentage_color_code}
                        />
                        <Progress
                          progress={e.contract_percentage}
                          title="+ договор"
                          color={e.contract_percentage_color_code}
                          tooltip={
                            <NumberFormat
                              value={e?.fact2_opt}
                              displayType="text"
                              thousandSeparator={" "}
                            />
                          }
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.left_sum}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.bonus_opt}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        {e.hunter_month_plan_id ? (
                          <button
                            type="button"
                            onClick={() => openPenalties(e)}
                            style={{
                              border: "none",
                              outline: "none",
                              background: "none",
                              textDecoration: "underline",
                              cursor: "pointer",
                              padding: "5px",
                            }}
                          >
                            <NumberFormat
                              value={e?.penalty_total_sum}
                              displayType="text"
                              thousandSeparator={" "}
                            />
                          </button>
                        ) : (
                          <NumberFormat
                            value={e?.penalty_total_sum}
                            displayType="text"
                            thousandSeparator={" "}
                          />
                        )}
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.hunter_salary}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.salary_n_bonuses}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <NumberFormat
                          value={e?.total}
                          displayType="text"
                          thousandSeparator={" "}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow className={classes.table_footer}>
                  <TableCell style={{ fontWeight: "bold" }}>Итого</TableCell>
                  <TableCell />
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.conversion_total_cnt}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.contracts_cnt}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    {`${stateSum?.conversion_pct ?? 0}%`}
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.hunter_month_plan_sum}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.fact_opt}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.fact_ddu}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell />
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.left_sum}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.bonus_opt}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.penalty_total_sum}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.hunter_salary}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.salary_n_bonuses}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberFormat
                      value={stateSum?.total}
                      displayType="text"
                      thousandSeparator={" "}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </EmptyOrShow>
      </Box>
    </Layout>
  );
};

export default HuntersKpiArchiveWrapper;

const sortData = (data, search = "") => {
  return data.filter(
    (el) =>
      el?.fio?.toLowerCase().includes(search.toLowerCase()) ||
      el?.hunter_month_plan_sum
        ?.toString()
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      el?.left_sum
        ?.toString()
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      el?.total
        ?.toString()
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      el?.fact_ddu
        ?.toString()
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      el?.salary_n_bonuses
        ?.toString()
        ?.toLowerCase()
        .includes(search.toLowerCase())
  );
};

const calcSum = (filteredData) => {
  const contracts_cnt = filteredData?.reduce(
    (a, b) => a + (b.contracts_cnt || 0),
    0
  );
  const conversion_total_cnt = filteredData?.reduce(
    (a, b) => a + (b.conversion_total_cnt || 0),
    0
  );
  return {
    bonus_opt: filteredData?.reduce((a, b) => a + (b.bonus_opt || 0), 0),
    fact2_opt: filteredData?.reduce((a, b) => a + (b.fact2_opt || 0), 0),
    fact_ddu: filteredData?.reduce((a, b) => a + (b.fact_ddu || 0), 0),
    fact_opt: filteredData?.reduce((a, b) => a + (b.fact_opt || 0), 0),
    hunter_month_plan_sum: filteredData?.reduce(
      (a, b) => a + (b.hunter_month_plan_sum || 0),
      0
    ),
    left_sum: filteredData?.reduce((a, b) => a + (b.left_sum || 0), 0),
    penalty_total_sum: filteredData?.reduce(
      (a, b) => a + (b.penalty_total_sum || 0),
      0
    ),
    hunter_salary: filteredData?.reduce(
      (a, b) => a + (b.hunter_salary || 0),
      0
    ),
    salary_n_bonuses: filteredData?.reduce(
      (a, b) => a + (b.salary_n_bonuses || 0),
      0
    ),
    total: filteredData?.reduce((a, b) => a + (b.total || 0), 0),
    contracts_cnt,
    conversion_total_cnt,
    conversion_pct: conversion_total_cnt
      ? Math.round((contracts_cnt / conversion_total_cnt) * 1000) / 10
      : 0,
  };
};

const headTitle = [
  "Отдел продаж",
  "Хантер",
  "Все",
  "Договор заключен",
  "Конверсия",
  "План",
  "Факт опционалки",
  "Факт ДДУ",
  "% выполнения плана",
  "Остаток плана",
  "Бонус опционалки",
  "Штраф",
  "Оклад",
  "Бонус + оклад",
  "К выплате (примерно)",
];
